import { useContext } from "react";
import { Anchor } from "../anchor";
import { Box } from "../box";
import { CopyButton } from "../copy-button";
import { Image } from "../image";
import { Text } from "../text";
import styles from "./markdown.module.scss";
import { MarkdownContext } from "./markdown.context";
import type { IMarkdownImageProps } from "./markdown.types";
import { COPY_FEEDBACK_TIMEOUT } from "./markdown.constants";
import { getAttachmentFileName } from "./markdown.utilities";

/**
 * Renders a markdown image, degrading to a static placeholder when the URL cannot be loaded.
 *
 * Attachments reach the interaction page as markdown in the message body — `![filename](url)` for images. Nothing can
 * make an unauthorised or expired URL load, so the only thing to get right is failing cleanly: name the attachment,
 * keep its URL readable, and never ask for it again.
 *
 * Whether this source has already failed is held by `Markdown`, above the markdown components, and read from context.
 * That placement is the point: it survives anything that remounts the rendered markdown, so a source that has failed
 * once is never rendered as an `<img>` again. There is then no element left to re-request it, `onError` cannot fire a
 * second time, and the `src` is never swapped or cleared — the degradation is terminal by construction.
 * @param props.src   The image URL from the markdown.
 * @param props.alt   The markdown alt text. Attachments are written as `![filename](url)`, so this is the filename.
 * @param props.node  The hast node `react-markdown` passes to every component. Dropped — it is not a DOM attribute.
 * @param props.ref   Dropped, as the other markdown element components do: the rendered image owns its own ref.
 * @returns The image, or a placeholder naming the attachment and showing its URL if the image failed to load.
 */
export const MarkdownImage = ({ node, ref, src, alt, ...otherProps }: IMarkdownImageProps) => {
    const { imageProps, failedImageSources, reportImageFailure } = useContext(MarkdownContext);

    if (typeof src === "undefined") {
        return null;
    }

    // `preview` selects the gallery-aware image below; it is not a DOM attribute or a Mantine `Image` property.
    const { preview, onError, ...otherImageProps } = imageProps ?? {};

    if (failedImageSources.has(src)) {
        // Spans rather than divs: markdown puts a standalone image inside a paragraph, and block elements there are
        // invalid nesting that the browser resolves by splitting the paragraph — a reflow, which is what to avoid.
        return (
            <Box className={styles["unrenderable-image"]} component="span" data-testid="markdown-unrenderable-image">
                <Text size="sm" span variant="semibold">
                    {getAttachmentFileName(src, alt)}
                </Text>
                <Text size="xs" span>
                    Unable to render this attachment.
                </Text>
                {/*
                 * The URL is deliberately not printed. These are signed attachment URLs of ~100 characters; spelled
                 * out they wrapped to three lines and dominated the message, and a truncated signed URL teaches the
                 * reader nothing anyway. The filename is the informative part, so it leads, and the URL is reachable
                 * by one click instead of by hand-selecting two lines of text.
                 *
                 * `copied` only turns true once the clipboard write resolves — `useClipboard` records a rejection in
                 * its `error` state and leaves `copied` false — so the label can never claim a copy that did not
                 * happen. A rejected write is silent rather than wrong, which is the safer of the two failures.
                 */}
                <CopyButton timeout={COPY_FEEDBACK_TIMEOUT} value={src}>
                    {({ copied, copy }) => (
                        <Anchor
                            // A real button, not an anchor: this performs an action rather than navigating, and it
                            // has to be reachable by keyboard.
                            //
                            // The colour cannot be left to inherit and cannot use the `c="neutral.9"` token form.
                            // Mantine's `Anchor` sets `color: var(--mantine-color-anchor)` on its own root, which
                            // beats the colour inherited from the container, and `c` resolves shade names through
                            // `theme.colors` — where `neutral` is not registered (its shades are emitted as bare
                            // CSS variables instead, so `--mantine-color-neutral-filled` does not exist). Measured
                            // on the interaction page: inheriting gives 3.37:1 and `c="neutral.9"` gives 3.37:1,
                            // both below the 4.5:1 AA floor; the variable form gives 14.63:1.
                            c="var(--mantine-color-neutral-9)"
                            component="button"
                            onClick={copy}
                            size="xs"
                            title={src}
                            type="button"
                        >
                            {copied ? "Copied" : "Copy link"}
                        </Anchor>
                    )}
                </CopyButton>
            </Box>
        );
    }

    const ImageComponent = preview === true ? Image.Preview.Image : Image;

    return (
        <ImageComponent
            src={src}
            alt={alt}
            {...otherProps}
            {...otherImageProps}
            onError={(event) => {
                onError?.(event);
                reportImageFailure(src);
            }}
        />
    );
};
